import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import * as userService from '../../services/userService';
import UserList from './UserList';
import UserModal from './UserModal';
import InviteManagement from './Invites/InviteManagement';
import CoachAssistantsManagement from './CoachAssistants/CoachAssistantsManagement';
import SettingsTab from './Settings/SettingsTab';
import { supabase } from '../../lib/supabase';
import { ResponsiveGrid, ResponsiveContainer, ResponsiveButton } from '../common/ResponsiveLayout';
import { useMobile } from '../../hooks/useMobile';

// Debounce hook for search
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const AdminPage = () => {
  const { user, userRole, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('users'); // 'users', 'invites', 'coach-assistants', or 'settings'

  // Search and pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [sortBy, setSortBy] = useState('first_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [roleCounts, setRoleCounts] = useState({ total: 0, coaches: 0, clients: 0 });

  // Debounce search to avoid too many API calls
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const fetchUsers = useCallback(async (options = {}) => {
    if (!user || !userRole) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const searchOptions = {
        page: currentPage,
        pageSize,
        search: debouncedSearchTerm,
        roleFilter,
        sortBy,
        sortOrder,
        ...options
      };

      const {
        data: fetchedUsers,
        error: fetchError,
        count,
        totalPages: pages
      } = await userService.getUsers(user.id, userRole, searchOptions);

      if (fetchError) {
        throw new Error(fetchError);
      }
      
      setUsers(fetchedUsers || []);
      setTotalCount(count || 0);
      setTotalPages(pages || 0);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [user, userRole, currentPage, pageSize, debouncedSearchTerm, roleFilter, sortBy, sortOrder]);

  const fetchRoleCounts = useCallback(async () => {
    if (!user || !userRole) return;
    
    try {
      const counts = await userService.getRoleCounts(user.id, userRole, {
        search: debouncedSearchTerm,
        roleFilter
      });
      
      if (counts.error) {
        console.error('Failed to fetch role counts:', counts.error);
      } else {
        setRoleCounts(counts);
      }
    } catch (err) {
      console.error('Failed to fetch role counts:', err);
    }
  }, [user, userRole, debouncedSearchTerm, roleFilter]);

  // Fetch users and role counts when dependencies change
  useEffect(() => {
    if (!authLoading && user && userRole) {
      fetchUsers();
      fetchRoleCounts();
    } else if (!authLoading && !user) {
      setLoading(false);
      setError("You must be logged in to view this page.");
    } else if (!authLoading && user && !userRole) {
      setLoading(false);
      setError("Could not determine user role. Please contact support.");
    }
  }, [authLoading, user, userRole, fetchUsers, fetchRoleCounts]);

  // Reset to first page when search or filters change
  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [debouncedSearchTerm, roleFilter]);

  // Search and filter handlers
  const handleSearchChange = (value) => {
    setSearchTerm(value);
  };

  const handleRoleFilterChange = (value) => {
    setRoleFilter(value);
  };

  const handleSortChange = (value) => {
    setSortBy(value);
  };

  const handleSortOrderChange = (value) => {
    setSortOrder(value);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setSortBy('created_at');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // User management handlers
  const handleAddUser = async (userData) => {
    setOperationLoading(true);
    setError(null);
    
    try {
      await userService.addUser(userData);
      await fetchUsers(); // Refresh the list
      await fetchRoleCounts(); // Refresh the counts
      setIsFormOpen(false); // Close form on success
      setSelectedUser(null);
    } catch (err) {
      console.error('Failed to add user:', err);
      setError(err.message || 'Failed to add user');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleUpdateUser = async (userId, updatedData) => {
    setOperationLoading(true);
    setError(null);
    
    try {
      // Handle password update if present (only for Super Admins)
      if (updatedData.password) {
        await userService.adminUpdateUserPassword(userId, updatedData.password);
        // Remove password from updatedData so it doesn't get passed to updateUser
        // (although updateUser ignores fields it doesn't know, it's cleaner to remove it)
        // We create a copy to avoid mutating the original object if it's used elsewhere
        const { password, ...restData } = updatedData;
        updatedData = restData;
      }

      // Only call updateUser if there are other fields to update
      if (Object.keys(updatedData).length > 0) {
        await userService.updateUser(userId, updatedData);
      }

      await fetchUsers(); // Refresh the list
      await fetchRoleCounts(); // Refresh the counts
      setSelectedUser(null); // Deselect user
      setIsFormOpen(false); // Close form
    } catch (err) {
      console.error('Failed to update user:', err);
      setError(err.message || 'Failed to update user');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    setOperationLoading(true);
    setError(null);
    
    try {
      await userService.deleteUser(userId);
      await fetchUsers(); // Refresh the list
      await fetchRoleCounts(); // Refresh the counts
    } catch (err) {
      console.error('Failed to delete user:', err);
      setError(err.message || 'Failed to delete user');
    } finally {
      setOperationLoading(false);
    }
  };

  const handleActivateDeactivate = async (user) => {
    const action = user.is_active ? 'deactivate' : 'activate';
    const actionText = user.is_active ? 'Deactivate' : 'Activate';
    const userName = user.first_name && user.last_name
      ? `${user.first_name} ${user.last_name}`
      : user.full_name || user.email || 'this user';

    if (!window.confirm(`Are you sure you want to ${action} ${userName}? ${user.is_active ? 'They will not be able to log in until reactivated.' : 'They will be able to log in again.'}`)) {
      return;
    }

    setOperationLoading(true);
    setError(null);
    
    try {
      await userService.activateDeactivateUser(user.id, !user.is_active);
      await fetchUsers(); // Refresh the list
      await fetchRoleCounts(); // Refresh the counts
    } catch (err) {
      console.error(`Failed to ${action} user:`, err);
      setError(err.message || `Failed to ${action} user`);
    } finally {
      setOperationLoading(false);
    }
  };

  const handleEdit = (userToEdit) => {
    setSelectedUser(userToEdit);
    setIsFormOpen(true);
    setError(null); // Clear any existing errors
  };
  
  const handleCancel = () => {
    setSelectedUser(null);
    setIsFormOpen(false);
    setError(null); // Clear any existing errors
  };
  
  const handleSave = async (formData) => {
    if (selectedUser) {
      await handleUpdateUser(selectedUser.id, formData);
    } else {
      await handleAddUser(formData);
    }
  };

  // Handle user profile view
  const handleViewUser = (user) => {
    navigate(`/admin/users/${user.id}`);
  };

  // Use role counts from the database instead of current page
  const stats = useMemo(() => ({
    total: roleCounts.total,
    coaches: roleCounts.coaches,
    clients: roleCounts.clients
  }), [roleCounts]);

  // Check if user has admin privileges
  const hasAdminAccess = userRole === 'SUPER_ADMIN';
  const hasCoachAccess = userRole === 'COACH';

  if (authLoading) {
    return (
      <div className="p-4">
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          You must be logged in to view this page.
        </div>
      </div>
    );
  }

  if (!hasAdminAccess && !hasCoachAccess) {
    return (
      <div className="p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          Access denied. You must be an admin or coach to access user management.
        </div>
      </div>
    );
  }

  return (
    <ResponsiveContainer>
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-gray-600 mt-1">
            {hasAdminAccess ? 'Manage users and invites in the system' : 'Manage your assigned clients and invites'}
          </p>
        </div>
        
        {activeTab === 'users' && hasAdminAccess && (
          <ResponsiveButton
            onClick={() => {
              setSelectedUser(null);
              setIsFormOpen(true);
              setError(null);
            }}
            variant="primary"
            disabled={operationLoading}
            size={{ xs: 'md', lg: 'md' }}
            className="w-full sm:w-auto"
          >
            Add New User
          </ResponsiveButton>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('users')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
              </svg>
              <span>Users</span>
            </div>
          </button>
          
          <button
            onClick={() => setActiveTab('invites')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'invites'
                ? 'border-indigo-500 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>Invites</span>
            </div>
          </button>

          {hasAdminAccess && (
            <>
              <button
                onClick={() => setActiveTab('coach-assistants')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'coach-assistants'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <span>Coach Assistants</span>
                </div>
              </button>

              <button
                onClick={() => setActiveTab('settings')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'settings'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Settings</span>
                </div>
              </button>
            </>
          )}
        </nav>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex">
            <div className="flex-1">
              <strong>Error:</strong> {error}
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-700 hover:text-red-900 ml-2 font-bold text-lg leading-none"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'users' ? (
        <div>
          {/* Stats Cards */}
          <ResponsiveGrid
            columns={{ xs: 3, sm: 3, md: 3 }}
            gap={4}
            className="mb-6"
          >
            <StatsCard
              value={stats.total}
              label="Total Users"
              color="gray"
            />
            <StatsCard
              value={stats.coaches}
              label={`Coaches${searchTerm || roleFilter ? ' (filtered)' : ''}`}
              color="blue"
            />
            <StatsCard
              value={stats.clients}
              label={`Clients${searchTerm || roleFilter ? ' (filtered)' : ''}`}
              color="green"
            />
          </ResponsiveGrid>

          {/* User List with Search and Pagination */}
          <UserList
            users={users}
            currentUserId={user?.id}
            userRole={userRole}
            onEdit={handleEdit}
            onDelete={handleDeleteUser}
            onView={handleViewUser}
            onActivateDeactivate={(hasAdminAccess || hasCoachAccess) ? handleActivateDeactivate : null}
            loading={loading}
            // Search and filter props
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
            roleFilter={roleFilter}
            onRoleFilterChange={handleRoleFilterChange}
            sortBy={sortBy}
            onSortChange={handleSortChange}
            sortOrder={sortOrder}
            onSortOrderChange={handleSortOrderChange}
            onClearFilters={handleClearFilters}
            // Pagination props
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />

          {/* User Modal */}
          <UserModal
            isOpen={isFormOpen}
            user={selectedUser}
            onSave={handleSave}
            onCancel={handleCancel}
            loading={operationLoading}
          />
        </div>
      ) : activeTab === 'invites' ? (
        <div>
          {/* Invite Management Component */}
          <InviteManagement />
        </div>
      ) : activeTab === 'coach-assistants' ? (
        <div>
          {/* Coach Assistants Management Component */}
          <CoachAssistantsManagement />
        </div>
      ) : activeTab === 'settings' ? (
        <div>
          <SettingsTab />
        </div>
      ) : null}
    </ResponsiveContainer>
  );
};

// Stats Card Component
const StatsCard = ({ value, label, color = 'gray' }) => {
  const colorClasses = {
    gray: 'text-gray-900',
    blue: 'text-blue-600',
    green: 'text-green-600',
    red: 'text-red-600',
    purple: 'text-purple-600'
  };

  return (
    <div className="bg-white shadow-sm rounded-lg p-4 border border-gray-200">
      <div className={`text-2xl font-bold ${colorClasses[color]}`}>
        {value}
      </div>
      <div className="text-sm text-gray-600 mt-1">
        {label}
      </div>
    </div>
  );
};

export default AdminPage;